import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { UserProfile } from '../types/userProfile';
import { useApiClient } from '../hooks/useApiClient';
import { envConfig, loginRequest } from '../config/authConfig';

const API_BASE_URL = envConfig.apiBaseUrl;

interface UserProfileContextType {
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  accessRestricted: boolean;
  fetchUserProfile: () => Promise<UserProfile | null>;
  clearProfile: () => void;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

// Module-level cache to prevent duplicate fetches across component mounts
let cachedProfile: UserProfile | null = null;
let fetchPromise: Promise<UserProfile | null> | null = null;
let photoFetchAttempted = false;

export function UserProfileProvider({ children }: { children: ReactNode }) {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(cachedProfile);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessRestricted, setAccessRestricted] = useState(false);
  

  const isAuthenticated = useIsAuthenticated();
  const { apiCall } = useApiClient();
  const { instance, accounts, inProgress } = useMsal();

  // Fetch profile photo from Microsoft Graph
  const fetchProfilePhoto = useCallback(async (): Promise<string | null> => {
    if (photoFetchAttempted) return cachedProfile?.photoUrl || null;
    
    const account = accounts[0];
    if (!account) return null;

    photoFetchAttempted = true;
    
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });

      const graphResponse = await fetch(`${envConfig.graphApiUrl}/me/photo/$value`, {
        headers: {
          Authorization: `Bearer ${response.accessToken}`,
        },
      });

      if (graphResponse.ok) {
        const blob = await graphResponse.blob();
        return URL.createObjectURL(blob);
      }
    } catch (error) {
      console.log('Profile photo fetch error:', error);
    }
    return null;
  }, [accounts, instance]);

  const fetchUserProfile = useCallback(async (): Promise<UserProfile | null> => {
    // Return cached profile immediately
    if (cachedProfile) {
      setUserProfile(cachedProfile);
      return cachedProfile;
    }

    // If fetch is in progress, wait for it
    if (fetchPromise) {
      const result = await fetchPromise;
      setUserProfile(result);
      return result;
    }

    setLoading(true);
    setError(null);

    fetchPromise = (async () => {
      try {
        const response = await apiCall(`${API_BASE_URL}/api/user/profile`);
        
        if (response.status === 403) {
          setAccessRestricted(true);
          throw new Error('Access restricted');
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch user profile: ${response.statusText}`);
        }

        const apiResponse = await response.json();
        
        if (apiResponse.success && apiResponse.data) {
          const photoUrl = await fetchProfilePhoto();
          
          const profileWithPhoto: UserProfile = {
            ...apiResponse.data,
            photoUrl: photoUrl || undefined
          };
          
          cachedProfile = profileWithPhoto;
          setUserProfile(profileWithPhoto);
          
          console.log('User profile loaded:', profileWithPhoto.displayName);
          return profileWithPhoto;
        } else {
          throw new Error(apiResponse.message || 'Failed to fetch user profile');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        console.error('Error fetching user profile:', err);
        return null;
      } finally {
        setLoading(false);
        fetchPromise = null;
      }
    })();

    return fetchPromise;
  }, [apiCall, fetchProfilePhoto]);

  const clearProfile = useCallback(() => {
    cachedProfile = null;
    fetchPromise = null;
    photoFetchAttempted = false;
    setUserProfile(null);
    setError(null);
    setAccessRestricted(false);
  }, []);

  // Auto-fetch profile when authenticated
  useEffect(() => {
    if (isAuthenticated && !cachedProfile && !fetchPromise && inProgress === InteractionStatus.None) {
      fetchUserProfile();
    }
  }, [isAuthenticated, fetchUserProfile, inProgress]);

  // Clear profile on logout
  useEffect(() => {
    if (!isAuthenticated) {
      clearProfile();
    }
  }, [isAuthenticated, clearProfile]);

  return (
    <UserProfileContext.Provider value={{ userProfile, loading, error, accessRestricted, fetchUserProfile, clearProfile }}>
      {children}
    </UserProfileContext.Provider>
  );
}

export function useUserProfileContext() {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error('useUserProfileContext must be used within a UserProfileProvider');
  }
  return context;
}
